import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild
} from "@angular/core";
import { NgModel } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatListOption, MatSelectionList } from "@angular/material/list";
import { DeviceCameraDTO, DeviceMicrophoneDTO, DeviceSpeakerDTO } from "@vp/models";
import { AssignColumnDef, AssignData, AssignFilterBar } from "@vp/virtual-care/data-access";
import { BehaviorSubject } from "rxjs";

@Component({
  selector: "vp-dynamic-assignment",
  standalone: false,
  templateUrl: "./dynamic-assignment.component.html",
  styleUrls: ["./dynamic-assignment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicAssignmentComponent implements OnInit, AfterViewInit {
  @ViewChild("selectionList") selectionList!: MatSelectionList;

  @Input() items: Record<string, any>[] = [];
  @Input() properties: string[] = [];
  @Input() columnDefs: AssignColumnDef[] = [];
  @Input() filterBar: AssignFilterBar[] = [];
  @Input() excludeProperties: string[] = [];
  @Input() multiple = false;
  @Input() title = "Assign";
  @Input() button = "Assign";
  @Input() noRecords = "No records available";
  @Input() selectedKey: string | null = null;

  muteChanged = false;
  volume = 0;
  columns: AssignColumnDef[] = [];
  selectedItemType: string | undefined = undefined;
  selectedItem: DeviceMicrophoneDTO | DeviceCameraDTO | DeviceSpeakerDTO | undefined = undefined;
  selectedLabelKey: string | undefined;
  selectedItems: Record<string, any>[] = [];
  originalItems: Record<string, any>[] = [];
  originalItem: MatListOption[] = [];
  disabled$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public assignData: AssignData,
    private dialogRef: MatDialogRef<DynamicAssignmentComponent>
  ) {}

  ngOnInit(): void {
    this.items = this.assignData.items ?? this.items;
    this.originalItems = this.items.map(item => ({ ...item }));
    this.properties = this.assignData.options?.properties ?? this.reflectProperties(this.items);
    this.columnDefs = this.assignData.options?.columnDefs ?? [];
    this.selectedItemType = this.columnDefs[0]?.header ?? undefined;
    this.columns = this.setColumnsFromProperties(this.properties, this.columnDefs);
    this.filterBar = this.assignData.options?.filterBar ?? [];
    this.selectedLabelKey = this.assignData.options?.selectedLabelKey;
    this.multiple = this.assignData.options?.multiple ?? this.multiple;
    this.title = this.assignData.options?.title ?? this.title;
    this.button = this.assignData.options?.button ?? this.button;
    this.noRecords = this.assignData.options?.noRecords ?? this.noRecords;
    this.selectedKey = this.assignData.options?.selectedKey ?? this.selectedKey;
  }

  ngAfterViewInit(): void {
    this.originalItem = this.selectionList.selectedOptions.selected;
  }

  onSubmit() {
    this.updateIsMuted();
    this.updateVolume();
    this.dialogRef.close({
      selected: this.selectionList.selectedOptions.selected,
      muteChanged: this.muteChanged
    });
  }

  preventNegative(event: KeyboardEvent) {
    if (event.key === "-") {
      event.preventDefault();
    }
  }

  checkMax(model: NgModel) {
    if (model.control) {
      if (this.volume > 100) {
        model.control.setErrors({ maxExceeded: true });
        this.disabled$.next(true);
      } else if (model.control.hasError("maxExceeded")) {
        model.control.setErrors(null);
        this.disabled$.next(false);
      } else {
        this.disabled$.next(false);
      }
    }
  }

  isMuted(item: any) {
    return item?.isMuted === "Yes" || item?.isMuted === true;
  }

  onToggle(state: "Yes" | "No") {
    const updatedItem = this.items.find(i => i.deviceName === this.selectedItem?.deviceName);
    if (updatedItem) updatedItem.isMuted = state;
  }

  onOptionSelected(item: any) {
    const index = this.selectedItems.indexOf(item);
    if (index === -1) {
      this.selectedItems.push(item);
      if (this.selectedItemType) {
        this.setSelectedItem(this.selectedItemType, item);
      }
    } else {
      this.selectedItems.splice(index, 1);
    }
  }

  isSelected(item: any) {
    if (this.selectedKey) {
      return item[this.selectedKey];
    }
    return this.selectedItems.indexOf(item) !== -1;
  }

  private setSelectedItem(type: string, item: any) {
    if (type === "Camera") {
      this.selectedItem = item as DeviceCameraDTO;
    } else if (type === "Microphone") {
      this.selectedItem = item as DeviceMicrophoneDTO;
    } else if (type === "Speaker") {
      this.selectedItem = item as DeviceSpeakerDTO;
      if ("masterVolumeScalar" in item) this.volume = Math.round(item.masterVolumeScalar * 100);
    }
  }

  private updateIsMuted() {
    const originalItem = this.originalItems.find(
      i => i.deviceName === this.selectedItem?.deviceName
    );
    if (
      this.selectedItem &&
      "isMuted" in this.selectedItem &&
      this.selectedItem.isMuted != originalItem?.isMuted
    ) {
      this.muteChanged = !this.muteChanged;
    }
  }

  private updateVolume() {
    if (this.selectedItem && "masterVolumeScalar" in this.selectedItem) {
      if (this.selectedItem.masterVolumeScalar != this.volume) {
        this.muteChanged = true;
      }
      this.selectedItem.masterVolumeScalar = this.volume;
    }
  }

  private reflectProperties(items: Record<string, any>[]): string[] {
    if (items?.length > 0) {
      return Object.keys(items[0]);
    } else {
      return [];
    }
  }

  private setColumnsFromProperties(
    properties: string[],
    columnDefs: AssignColumnDef[]
  ): AssignColumnDef[] {
    let columns: AssignColumnDef[] = [];

    columns = properties.map(prop => {
      const colDef = columnDefs.find(item => item.key === prop);

      if (colDef) {
        return {
          key: prop,
          width: colDef.width,
          header: colDef.header
        } as AssignColumnDef;
      } else {
        return {
          key: prop,
          width: 100 / properties.length
        } as AssignColumnDef;
      }
    });

    return columns;
  }
}
